import {toastr} from 'react-redux-toastr';
import {
  initialize,
  isDirty
} from 'redux-form';
import {
  calculateBalanceDue,
  cancelEntryDetails,
  cancelEntryPaymentLogHistory,
  cancelPurchasedEntryForm,
  closeVirtualEntryEditing,
  cancelVirtualEventPaymentLogHistory,
  clearCartItems,
  clearSchedulerItems,
  displayClearCartConfirmation,
  displayCountriesInDropDown,
  displayEntries,
  displayEntryDetails,
  displayPurchasedEntryForm,
  displayRosterPositions,
  displayStatesInDropDown,
  displayTeamOwnedVirtualEntries,
  displayVirtualEntries,
  displayVirtualEntryBowlerList,
  handleRemoveUnassignedChildVirtualEntries,
  handleRemoveUnassignedParentVirtualEntries,
  setAvailableBowlersInState,
  setAvailableBowlersInStateForVirtualEntries,
  setBowlerForVirtualEntry,
  setEntryDetailsInState,
  setEntryStateDisabled,
  setRemoveVirtualEntryConfirmationVisibility,
  setRosterConfirmationVisibility,
  setVirtualEntryBowlerList,
  setVirtualEventSelectedEntry,
  virtualEntrySubmittingFinished,
  virtualEntrySubmittingStarts
} from './actionTypes';
import {
  handleOnVirtualEventQuantityChange,
  removeVirtualEventFromCart
} from '../checkout/actions';
import {
  checkForCompleteRosters 
} from '../rostercheckout/actions';
import {
  setCaptRosterWarnings 
} from '../manage/actionTypes';
import {
  getJsonAsync,
  putJsonAsync,
  navigateTo
} from 'shared/actions';
import {
  setReturningToCalendarFlag
} from '../actionTypes';
import { displayErrorList } from '../../shared/error/errorManager';

export const cancelCheckout = () => (dispatch, getState) => {
  const {tournamentId} = getState().registration.base;

  dispatch(getJsonAsync(`registration/cartroster/clear/${tournamentId}`));
  delete localStorage.cart;
  if (localStorage.tempCartEntries)
    delete localStorage.tempCartEntries;
  dispatch(clearSchedulerItems());
  dispatch(clearCartItems());
  dispatch(displayClearCartConfirmation(false));
  dispatch(navigateTo(`registration/schedule/${tournamentId}`));
};

export const handleContinueShoppingClick = () => (dispatch, getState) => {
  const {tournamentId} = getState().registration.base;
  
  dispatch(setReturningToCalendarFlag());
  dispatch(navigateTo(`registration/schedule/${tournamentId}`));
};

export const retrieveEntries = tournamentId => dispatch => new Promise(resolve => {
  dispatch(getJsonAsync(`tournament/${tournamentId}/entries`))
  .then(data => {
    dispatch(displayEntries(data.map(entry => ({
      ...entry,
      selected: !!entry.balanceDue && !entry.isCancelled
    }))));
    dispatch(calculateBalanceDue());
    resolve();
  });
});

export const retrieveUserVirtualEntries = tournamentId => dispatch => new Promise((resolve) => {
  dispatch(getJsonAsync(`tournament/${tournamentId}/virtualentries`))
  .then(data => {
    dispatch(displayVirtualEntries(data.map(virtualEvent => ({
      ...virtualEvent,
      selected: !!virtualEvent.balanceDue
    }))));
    dispatch(calculateBalanceDue());
  }).then(() => resolve());
});

export const retrieveTeamOwnedVirtualEntries = (eventId, entryId, bowlerCount) => dispatch => {
  dispatch(getJsonAsync(`registration/virtualevent/${eventId}/virtualentries`))
  .then(data => {
    data.forEach(item => {
      item.eventId = eventId;
      item.isPurchased = true;
    });
    dispatch(displayTeamOwnedVirtualEntries(data, entryId, bowlerCount));
  });
};

export const retrieveStates = () => dispatch =>{
  dispatch(getJsonAsync(`states`))
    .then(states => dispatch(displayStatesInDropDown(states)));
};

export const retrieveCountries = () => dispatch =>{
  dispatch(getJsonAsync(`countries`))
    .then(countries => (
      dispatch(displayCountriesInDropDown(countries.map(({id, name}) => ({
        value: id,
        label: name
      }))))));
};

export const handleVirtualEntryManageRosterClick = virtualEventId => (dispatch, getState) => {
  // prevent user to loose changes while editing an entry
  if(dispatch(hasPendingRosterChanges(handleVirtualEntryManageRosterClick.name, virtualEventId))) { return; }

  const tournamentId = getState().registration.base.tournamentId;

  dispatch(getJsonAsync(`virtualevent/${virtualEventId}/bowlers`))
  .then(bowlers => {
    // close other panels
    dispatch(cancelEntryDetails());
    dispatch(cancelPurchasedEntryForm());
    dispatch(cancelEntryPaymentLogHistory());
    dispatch(cancelVirtualEventPaymentLogHistory());
    // display new panel
    dispatch(setVirtualEntryBowlerList(bowlers.map(x => ({
      ...x,
      bowlerId: x.bowlerId || ''
    }))));
    dispatch(retrieveAvailableBowlersForVirtualEntries(tournamentId, virtualEventId))
    .then(() => dispatch(displayVirtualEntryBowlerList(virtualEventId)));
  });
};

export const handleVirtualEventSelectChecked = virtualEntryId => dispatch => {
  dispatch(setVirtualEventSelectedEntry(virtualEntryId));
  dispatch(calculateBalanceDue());
};

export const handleViewRosterClick = entryId => dispatch => {
  // prevent user to loose changes while editing an entry
  if(dispatch(hasPendingRosterChanges(handleViewRosterClick.name, entryId))) { return; }

  dispatch(getJsonAsync(`registration/entry/${entryId}/readonly`))
  .then(entry => {
    // close other panels
    dispatch(cancelPurchasedEntryForm());
    dispatch(cancelEntryPaymentLogHistory());
    dispatch(cancelVirtualEventPaymentLogHistory());
    dispatch(closeVirtualEntryEditing());
    // display new panel
    dispatch(setEntryDetailsInState(entry));
    dispatch(displayEntryDetails(entryId));
  });
};

export const handleManageRosterClick = entryId => (dispatch, getState) => {
  // prevent user to loose changes while editing an entry
  if(dispatch(hasPendingRosterChanges(handleManageRosterClick.name, entryId))) { return; }

  const {initialCountry, countries} = getState().registration.shared;

  dispatch(getJsonAsync(`registration/entry/${entryId}`))
  .then(entry => {
    const selectedCountry = countries.find(x => x.value === (entry.countryId || initialCountry)),
      disableEntryState = !selectedCountry || !selectedCountry.label || selectedCountry.label !== 'United States';
    let rosterPositionsAverage = {};
    const rosterPositions = entry.rosterPositions.map(roster => ({
      ...roster,
      ...{
        _searchValue: roster.bowler.usbcId || '',
        _processing: false,
        _retrieved: !!roster.bowler.usbcId && !roster.bowler.error,
        _errorMessage: roster.bowler.error ? roster.bowler.error.Message : null,
        bowler: roster.bowler.error ? {} : {
          ...roster.bowler,
          average: roster.bowler.average || ''
        }
      }
    }));

    const errorUsbcIds = rosterPositions.filter(rosterPosition => rosterPosition._errorMessage).map(rosterPosition => rosterPosition._searchValue);

    if (errorUsbcIds && errorUsbcIds.length > 0) {
      toastr.error(`USBC ID${errorUsbcIds.length === 1 ? '' : 's'} ${errorUsbcIds.join()} ${errorUsbcIds.length === 1 ? 'is' : 'are'} not found in membership`, '', { allowHtml: true });
    }
    // close other panels
    dispatch(cancelEntryDetails());
    dispatch(cancelEntryPaymentLogHistory());
    dispatch(cancelVirtualEventPaymentLogHistory());
    dispatch(closeVirtualEntryEditing());
    // display new panel
    dispatch(displayRosterPositions(rosterPositions));
    // generates redux-form model for roster positions averages
    rosterPositions.forEach(roster => {
      rosterPositionsAverage[`average-${roster.position}`] = roster.bowler.average;
      rosterPositionsAverage[`usbcId-${roster.position}`] = roster.bowler.usbcId;
      rosterPositionsAverage[`isProfessional-${roster.position}`] = roster.bowler.isProfessional;
    });
    // display form
    dispatch(displayPurchasedEntryForm(entryId));

    // display team-owned virtual entries
    dispatch(retrieveTeamOwnedVirtualEntries(entry.eventId, entry.id, entry.rosterPositions.length));
  
      // re-initialize redux-form with entry data
    dispatch(initialize('entryForm', {
      ...rosterPositionsAverage,
      id: entry.id,
      name: entry.name,
      countryId: !entry.countryId ? initialCountry : entry.countryId,
      state: entry.state,
      rosterPositionsQty: rosterPositions.length,
      pairCode: entry.pairCode,
      groupCode: entry.groupCode
    }));
     
    dispatch(setCaptRosterWarnings(undefined));

    dispatch(setEntryStateDisabled(entryId, disableEntryState));

    const tournamentId = getState().registration.base.tournamentId;

    // initialize available bowler list for doubles/singles dropwdown
    dispatch(retrieveAvailableBowlers(tournamentId, entry.eventId));
  });
};

export const handleUpdateVirtualEntry = virtualEventId => (dispatch, getState) => {
  const {registration: regState} = getState();
  const {virtualBowlers: virtualEntryBowlers} = regState.shared;
  const {tournamentId} = regState.base;
 
  dispatch(virtualEntrySubmittingStarts(virtualEventId));
  dispatch(putJsonAsync('virtualevent/bowlers', {
    virtualEventId,
    virtualEntryBowlers
  }, false))
  .then(() => {
    dispatch(virtualEntrySubmittingFinished(virtualEventId));
    dispatch(retrieveUserVirtualEntries(tournamentId)).then(() => dispatch(checkForCompleteRosters()));
    toastr.success('Saved!', 'Virtual entries successfully saved.');
  })
  .catch((error) => {
    dispatch(virtualEntrySubmittingFinished(virtualEventId));
    
    if(!Array.isArray(error))
    {
      error = [ error.message ];
    }

    dispatch(displayErrorList(error));
  });
};

export const handleVirtualEntryBowlerChange = (virtualEntryId, bowlerId) => (dispatch) => {
  dispatch(setBowlerForVirtualEntry(virtualEntryId, bowlerId));
};

export const hasPendingRosterChanges = (nextAction, args) => (dispatch, getState) => {
  const entries = getState().registration.shared.entries,
    pendingChanges = isDirty('entryForm')(getState());

  // prevent user to loose changes while editing an entry
  if(entries.some(x => x.editing) && pendingChanges) {
    dispatch(setRosterConfirmationVisibility(true, nextAction, args));
    return true;
  }
  return false;
};

export const retrieveAvailableBowlers = (tournamentId, eventId) => dispatch => new Promise((resolve, reject) => {
  dispatch(getJsonAsync(`tournament/${tournamentId}/bowlers/${eventId}`))
  .then(bowlers => {
    dispatch(setAvailableBowlersInState(bowlers));
    resolve(bowlers);
  })
  .catch(error => reject(error));
});

export const retrieveAvailableBowlersForVirtualEntries = (tournamentId, virtualEventId) => dispatch => new Promise((resolve, reject) => {
  dispatch(getJsonAsync(`tournament/${tournamentId}/virtualbowlers/${virtualEventId}`))
  .then(bowlers => {
    dispatch(setAvailableBowlersInStateForVirtualEntries(bowlers));
    resolve(bowlers);
  })
  .catch(error => reject(error));
});

export const handleRemoveUnassignedVirtualEntries = () => (dispatch, getState) => {
  const {shared,payment} = getState().registration;
  const virtualEntryToRemove = shared.virtualEntryToRemove;
  const childTeamOwnedVirtualEntries = shared.childTeamOwnedVirtualEntries;
  const parentTeamOwnedVirtualEntries = shared.parentTeamOwnedVirtualEntries;
  const cartVirtualEventToRemove = payment.cart.virtualEvents.find(x => x.name === virtualEntryToRemove.name);

  if(virtualEntryToRemove.childOrParent === 'child'){
    const childrenToRemove = childTeamOwnedVirtualEntries.filter(x => x.virtualEventName === virtualEntryToRemove.name && x.parentTeamEntryId === null);
    const virtualEventId = childrenToRemove[0].virtualEventId;
    const newQuantity = cartVirtualEventToRemove.quantity - childrenToRemove.length;

    if(newQuantity == 0) {
      dispatch(removeVirtualEventFromCart(virtualEventId));
    } else {
      dispatch(handleOnVirtualEventQuantityChange({virtualEventId: virtualEventId, inputValue: newQuantity}));
    }
    dispatch(handleRemoveUnassignedChildVirtualEntries(virtualEntryToRemove.name));
  } else {
    const parentsToRemove = parentTeamOwnedVirtualEntries.filter(x => x.virtualEventName === virtualEntryToRemove.name && x.teamEntryId === null);
    const virtualEventId = parentsToRemove[0].virtualEventId;
    const newQuantity = cartVirtualEventToRemove.quantity - parentsToRemove.length;

    if(newQuantity == 0) {
      dispatch(removeVirtualEventFromCart(virtualEventId));
    } else {
      dispatch(handleOnVirtualEventQuantityChange({virtualEventId: virtualEventId, inputValue: newQuantity}));
    }
    dispatch(handleRemoveUnassignedParentVirtualEntries(virtualEntryToRemove.name));
  }
  dispatch(setRemoveVirtualEntryConfirmationVisibility(false));
  dispatch(checkForCompleteRosters());
};



// WEBPACK FOOTER //
// ./src/features/registration/shared/actions.js